import React, { useState, useMemo, useRef, useEffect } from 'react';
import { T, fmtMoney, fmtNum, safeNum, fmtMonth, healthColor, healthLabel, calcRiskScore, calcClientGroup, getGroupConfig, TRADER_TYPES, TRADER_COLORS, TRADER_ICONS } from '../theme';
import { Card, CoBadge, CustomTooltip } from '../components/UI';
import { Search, X, AlertTriangle } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

// ═══ ALL SUB-COMPONENTS OUTSIDE MAIN (prevents input focus loss) ═══

function Picker({ label, val, setVal, client, setClient, results, focused, setFocused, innerRef, color }) {
  return (
    <div style={{ flex: 1 }} ref={innerRef}>
      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{label}</div>
      <div style={{ position: "relative" }}>
        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textMuted, zIndex: 2 }} />
        <input value={val}
          onChange={e => { setVal(e.target.value); if (client) setClient(null); }}
          onFocus={() => setFocused(true)}
          placeholder="Type login number or client name..."
          style={{ width: "100%", padding: "10px 36px 10px 32px", borderRadius: 8, border: `1px solid ${client ? color + "60" : T.border}`, background: client ? color + "08" : T.bgCard, color: T.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
        {val && <X size={14} onClick={() => { setVal(""); setClient(null); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: T.textMuted, cursor: "pointer", zIndex: 2 }} />}
        {focused && val && !client && results.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, zIndex: 20, maxHeight: 280, overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
            {results.map(r => (
              <div key={(r._company || "") + r.Login} onClick={() => { setClient(r); setVal("#" + r.Login + " " + (r.Name || "")); setFocused(false); }}
                style={{ padding: "10px 14px", cursor: "pointer", fontSize: 12, borderBottom: `1px solid ${T.border}15`, display: "flex", alignItems: "center", gap: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {r._company && <CoBadge co={r._company} size={14} />}
                <span style={{ color, fontWeight: 700 }}>#{r.Login}</span>
                <span style={{ color: T.text, flex: 1 }}>{r.Name || "—"}</span>
                <span style={{ color: T.textMuted, fontSize: 10 }}>{r.Account_Type || ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {client && (
        <div style={{ marginTop: 8, padding: "10px 14px", background: color + "12", borderRadius: 8, border: `1px solid ${color}25`, display: "flex", alignItems: "center", gap: 10 }}>
          {client._company && <CoBadge co={client._company} size={16} />}
          <span style={{ color, fontWeight: 700, fontSize: 14 }}>#{client.Login}</span>
          <span style={{ color: T.text, fontSize: 13, flex: 1 }}>{client.Name || "—"}</span>
          <span style={{ fontSize: 10, color: T.textMuted, background: T.bg, padding: "2px 8px", borderRadius: 4 }}>{client.Account_Type || "—"}</span>
        </div>
      )}
    </div>
  );
}

// higher="risk" → higher=red. higher="good" → higher=green. higher="neutral" → no color.
function CompareRow({ label, a, b, format, higher = "risk", icon }) {
  const fa = format ? format(a) : (a ?? "—");
  const fb = format ? format(b) : (b ?? "—");
  const na = typeof a === "number" ? a : NaN;
  const nb = typeof b === "number" ? b : NaN;
  let cA = T.text, cB = T.text, bA = "transparent", bB = "transparent";
  if (!isNaN(na) && !isNaN(nb) && na !== nb) {
    if (higher === "risk") {
      if (na > nb) { cA = T.red; bA = T.redBg; cB = T.green; bB = T.greenBg; }
      else { cB = T.red; bB = T.redBg; cA = T.green; bA = T.greenBg; }
    } else if (higher === "good") {
      if (na > nb) { cA = T.green; bA = T.greenBg; cB = T.red; bB = T.redBg; }
      else { cB = T.green; bB = T.greenBg; cA = T.red; bA = T.redBg; }
    }
  }
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.border}10` }}>
      <div style={{ flex: 1, textAlign: "right", padding: "4px 12px", borderRadius: 4, background: bA }}><span className="mono" style={{ color: cA, fontWeight: 600, fontSize: 13 }}>{fa}</span></div>
      <div style={{ width: 160, textAlign: "center", color: T.textDim, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>{icon}{label}</div>
      <div style={{ flex: 1, padding: "4px 12px", borderRadius: 4, background: bB }}><span className="mono" style={{ color: cB, fontWeight: 600, fontSize: 13 }}>{fb}</span></div>
    </div>
  );
}

function TextRow({ label, a, b }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.border}10` }}>
      <div style={{ flex: 1, textAlign: "right", padding: "4px 12px" }}><span style={{ color: T.text, fontSize: 13 }}>{a || "—"}</span></div>
      <div style={{ width: 160, textAlign: "center", color: T.textDim, fontSize: 11 }}>{label}</div>
      <div style={{ flex: 1, padding: "4px 12px" }}><span style={{ color: T.text, fontSize: 13 }}>{b || "—"}</span></div>
    </div>
  );
}

function SectionHeader({ title, color }) {
  return (
    <div style={{ padding: "10px 0 6px", borderBottom: `2px solid ${color}30`, marginTop: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color }}>{title}</span>
    </div>
  );
}

function FlagRow({ client }) {
  const flags = [...TRADER_TYPES, "IP_Abuser"].filter(f => client[f] === f);
  return flags.length > 0 ? (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {flags.map(f => (
        <span key={f} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: (TRADER_COLORS[f] || T.red) + "20", color: TRADER_COLORS[f] || T.red }}>
          {TRADER_ICONS[f] || "⚡"} {f}
        </span>
      ))}
    </div>
  ) : <span style={{ fontSize: 11, color: T.textMuted }}>No flags</span>;
}

function RiskCard({ client, risk, groupResult, color, gcfg }) {
  const gInfo = gcfg.groups[groupResult.group] || {};
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${color}25`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: `linear-gradient(135deg, ${healthColor(risk.total)}30, ${healthColor(risk.total)}08)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: healthColor(risk.total) }}>{risk.total}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color }}>{client.Name || "#" + client.Login}</div>
          <div style={{ fontSize: 11, color: T.textMuted }}>{healthLabel(risk.total)} Risk — {client.ClientStatus || "—"}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 2, height: 6, borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
        <div style={{ width: `${(risk.breakdown.revenue / 100) * 100}%`, background: "#ef4444" }} />
        <div style={{ width: `${(risk.breakdown.position / 100) * 100}%`, background: "#06b6d4" }} />
        <div style={{ width: `${(risk.breakdown.compliance / 100) * 100}%`, background: "#f59e0b" }} />
        <div style={{ width: `${(risk.breakdown.strategy / 100) * 100}%`, background: "#a855f7" }} />
      </div>
      <div style={{ display: "flex", gap: 8, fontSize: 10, color: T.textDim, marginBottom: 10 }}>
        <span><span style={{ color: "#ef4444" }}>●</span> Rev {risk.breakdown.revenue}/35</span>
        <span><span style={{ color: "#06b6d4" }}>●</span> Pos {risk.breakdown.position}/10</span>
        <span><span style={{ color: "#f59e0b" }}>●</span> Comp {risk.breakdown.compliance}/30</span>
        <span><span style={{ color: "#a855f7" }}>●</span> Str {risk.breakdown.strategy}/25</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: T.textMuted }}>Group:</span>
        <span style={{ padding: "3px 10px", borderRadius: 4, background: (gInfo.color || T.cyan) + "20", color: gInfo.color || T.cyan, fontSize: 11, fontWeight: 700 }}>{gInfo.name || groupResult.group}</span>
        {groupResult.isArbitrage && <span style={{ padding: "3px 8px", borderRadius: 4, background: T.redBg, color: T.red, fontSize: 10, fontWeight: 700 }}>⚠ ARBITRAGE</span>}
      </div>
      <FlagRow client={client} />
    </div>
  );
}

// ═══ MAIN COMPONENT ═══

export default function ComparisonTool({ C }) {
  const [searchA, setSearchA] = useState("");
  const [searchB, setSearchB] = useState("");
  const [clientA, setClientA] = useState(null);
  const [clientB, setClientB] = useState(null);
  const [focusA, setFocusA] = useState(false);
  const [focusB, setFocusB] = useState(false);
  const refA = useRef(null);
  const refB = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (refA.current && !refA.current.contains(e.target)) setFocusA(false);
      if (refB.current && !refB.current.contains(e.target)) setFocusB(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = (q) => {
    if (!q || q.length < 1) return [];
    const ql = q.toLowerCase();
    return C.clients.filter(c =>
      String(c.Login).includes(q) ||
      (c.Name || "").toLowerCase().includes(ql) ||
      (c.CU_Account && String(c.CU_Account).includes(q))
    ).slice(0, 12);
  };

  const resultsA = useMemo(() => search(searchA), [searchA, C.clients]);
  const resultsB = useMemo(() => search(searchB), [searchB, C.clients]);

  const gcfg = getGroupConfig();
  const riskA = useMemo(() => clientA ? calcRiskScore(clientA) : null, [clientA]);
  const riskB = useMemo(() => clientB ? calcRiskScore(clientB) : null, [clientB]);
  const groupA = useMemo(() => clientA && riskA ? calcClientGroup(clientA, riskA) : null, [clientA, riskA]);
  const groupB = useMemo(() => clientB && riskB ? calcClientGroup(clientB, riskB) : null, [clientB, riskB]);

  const timelines = useMemo(() => {
    if (!clientA || !clientB) return { volume: [], profit: [], risk: [], group: [] };
    const getH = (c) => C.historyMap?.[c.Login] || C.historyMap?.[(c._company || "") + "|" + c.Login] || [];
    const hA = getH(clientA), hB = getH(clientB);
    const allMonths = [...new Set([...hA.map(h => h.MonthKey), ...hB.map(h => h.MonthKey)])].sort();

    const volume = allMonths.map(m => {
      const a = hA.find(h => h.MonthKey === m), b = hB.find(h => h.MonthKey === m);
      return { month: fmtMonth(m), volA: a ? safeNum(a.Volume) : null, volB: b ? safeNum(b.Volume) : null };
    });

    const profit = allMonths.map(m => {
      const a = hA.find(h => h.MonthKey === m), b = hB.find(h => h.MonthKey === m);
      return { month: fmtMonth(m), profA: a ? safeNum(a.Profit) : null, profB: b ? safeNum(b.Profit) : null };
    });

    const risk = allMonths.map(m => {
      const a = hA.find(h => h.MonthKey === m), b = hB.find(h => h.MonthKey === m);
      return { month: fmtMonth(m), scoreA: a ? calcRiskScore(a).total : null, scoreB: b ? calcRiskScore(b).total : null };
    });

    const group = allMonths.map(m => {
      const a = hA.find(h => h.MonthKey === m), b = hB.find(h => h.MonthKey === m);
      const grA = a ? calcClientGroup(a, calcRiskScore(a)) : null;
      const grB = b ? calcClientGroup(b, calcRiskScore(b)) : null;
      return { month: fmtMonth(m), groupA: grA?.group || null, groupB: grB?.group || null };
    });

    return { volume, profit, risk, group };
  }, [clientA, clientB, C.historyMap]);

  const radarData = useMemo(() => {
    if (!riskA || !riskB) return [];
    return [
      { cat: "Revenue", A: riskA.breakdown.revenue, B: riskB.breakdown.revenue },
      { cat: "Position", A: riskA.breakdown.position, B: riskB.breakdown.position },
      { cat: "Compliance", A: riskA.breakdown.compliance, B: riskB.breakdown.compliance },
      { cat: "Strategy", A: riskA.breakdown.strategy, B: riskB.breakdown.strategy },
    ];
  }, [riskA, riskB]);

  const nameA = clientA ? (clientA.Name || "#" + clientA.Login) : "";
  const nameB = clientB ? (clientB.Name || "#" + clientB.Login) : "";

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, color: T.text }}>Client Comparison</h1>

      <div style={{ display: "flex", gap: 20, marginBottom: 24 }}>
        <Picker label="Client A" val={searchA} setVal={setSearchA} client={clientA} setClient={setClientA} results={resultsA} focused={focusA} setFocused={setFocusA} innerRef={refA} color={T.cyan} />
        <Picker label="Client B" val={searchB} setVal={setSearchB} client={clientB} setClient={setClientB} results={resultsB} focused={focusB} setFocused={setFocusB} innerRef={refB} color={T.amber} />
      </div>

      {clientA && clientB && riskA && riskB && groupA && groupB ? (
        <div>
          {/* Risk Summary Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <RiskCard client={clientA} risk={riskA} groupResult={groupA} color={T.cyan} gcfg={gcfg} />
            <RiskCard client={clientB} risk={riskB} groupResult={groupB} color={T.amber} gcfg={gcfg} />
          </div>

          {/* Who is riskier */}
          {riskA.total !== riskB.total && (
            <div style={{ background: T.redBg, border: `1px solid ${T.red}30`, borderRadius: 10, padding: "12px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <AlertTriangle size={18} color={T.red} />
              <span style={{ fontSize: 13, color: T.red, fontWeight: 600 }}>
                {riskA.total > riskB.total ? nameA : nameB} is riskier
              </span>
              <span style={{ fontSize: 12, color: T.textDim }}>
                (Score {Math.max(riskA.total, riskB.total)} vs {Math.min(riskA.total, riskB.total)} — {Math.abs(riskA.total - riskB.total)} pts difference)
              </span>
            </div>
          )}

          {/* Comparison Table */}
          <Card style={{ marginBottom: 20 }}>
            {(() => {
              // Count ALL active flags per client
              const allFlagTypes = [...TRADER_TYPES, "IP_Abuser"];
              const flagCountA = allFlagTypes.filter(f => clientA[f] === f).length;
              const flagCountB = allFlagTypes.filter(f => clientB[f] === f).length;
              // Count red risky values
              const metrics = [
                [riskA.total, riskB.total, "risk"], [riskA.breakdown.revenue, riskB.breakdown.revenue, "risk"],
                [riskA.breakdown.position, riskB.breakdown.position, "risk"], [riskA.breakdown.compliance, riskB.breakdown.compliance, "risk"],
                [riskA.breakdown.strategy, riskB.breakdown.strategy, "risk"], [flagCountA, flagCountB, "risk"],
                [safeNum(clientA.Equity), safeNum(clientB.Equity), "risk"], [safeNum(clientA.Profit), safeNum(clientB.Profit), "risk"],
                [safeNum(clientA.Volume), safeNum(clientB.Volume), "risk"], [safeNum(clientA.ProfitFactor), safeNum(clientB.ProfitFactor), "risk"],
                [safeNum(clientA.ProfitPct), safeNum(clientB.ProfitPct), "risk"], [safeNum(clientA.BestTrade), safeNum(clientB.BestTrade), "risk"],
                [safeNum(clientA.WorstTrade), safeNum(clientB.WorstTrade), "risk"],
                [safeNum(clientA["T.Volume"]), safeNum(clientB["T.Volume"]), "risk"], [safeNum(clientA["T.Profit"]), safeNum(clientB["T.Profit"]), "risk"],
                [safeNum(clientA.TotalTrades), safeNum(clientB.TotalTrades), "risk"],
                [safeNum(clientA.PctUnder5Min), safeNum(clientB.PctUnder5Min), "risk"],
                [safeNum(clientA.HedgeRatio), safeNum(clientB.HedgeRatio), "risk"],
                [safeNum(clientA.HedgeVolumeLots), safeNum(clientB.HedgeVolumeLots), "risk"],
                [safeNum(clientA.FridayHedgeBreaks), safeNum(clientB.FridayHedgeBreaks), "risk"],
                [safeNum(clientA.LongPct), safeNum(clientB.LongPct), "risk"],
                [safeNum(clientA.MaxConsecutiveWins), safeNum(clientB.MaxConsecutiveWins), "risk"],
                [safeNum(clientA.DailyVolumeLots), safeNum(clientB.DailyVolumeLots), "risk"],
                [safeNum(clientA.TradingFreqPerDay), safeNum(clientB.TradingFreqPerDay), "risk"],
                [safeNum(clientA.AvgTradeDuration), safeNum(clientB.AvgTradeDuration), "good"],
                [safeNum(clientA.MostUsedTradeSize), safeNum(clientB.MostUsedTradeSize), "risk"],
              ];
              let redA = 0, redB = 0;
              metrics.forEach(([a, b, h]) => {
                if (isNaN(a) || isNaN(b) || a === b) return;
                if (h === "risk") { if (a > b) redA++; else redB++; }
                else { if (a < b) redA++; else redB++; }
              });
              return (
            <div style={{ display: "flex", padding: "12px 0", borderBottom: `2px solid ${T.border}`, marginBottom: 4 }}>
              <div style={{ flex: 1, textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                <span style={{ color: T.cyan, fontWeight: 700, fontSize: 14 }}>{nameA}</span>
                <span style={{ padding: "2px 8px", borderRadius: 10, background: T.redBg, color: T.red, fontSize: 10, fontWeight: 700 }}>{redA} red</span>
              </div>
              <div style={{ width: 160, textAlign: "center", color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Metric</div>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: T.amber, fontWeight: 700, fontSize: 14 }}>{nameB}</span>
                <span style={{ padding: "2px 8px", borderRadius: 10, background: T.redBg, color: T.red, fontSize: 10, fontWeight: 700 }}>{redB} red</span>
              </div>
            </div>
              );
            })()}

            <SectionHeader title="Risk Assessment" color={T.red} />
            <CompareRow label="Risk Score" a={riskA.total} b={riskB.total} format={v => v} higher="risk" icon={<AlertTriangle size={11} />} />
            <CompareRow label="Revenue Risk" a={riskA.breakdown.revenue} b={riskB.breakdown.revenue} format={v => v + " /35"} higher="risk" />
            <CompareRow label="Position Risk" a={riskA.breakdown.position} b={riskB.breakdown.position} format={v => v + " /10"} higher="risk" />
            <CompareRow label="Compliance" a={riskA.breakdown.compliance} b={riskB.breakdown.compliance} format={v => v + " /30"} higher="risk" />
            <CompareRow label="Strategy Risk" a={riskA.breakdown.strategy} b={riskB.breakdown.strategy} format={v => v + " /25"} higher="risk" />
            {(() => { const af = [...TRADER_TYPES, "IP_Abuser"]; return <CompareRow label="Active Flags" a={af.filter(f => clientA[f] === f).length} b={af.filter(f => clientB[f] === f).length} format={v => v} higher="risk" />; })()}

            <SectionHeader title="Financial Performance" color={T.green} />
            <CompareRow label="Equity" a={safeNum(clientA.Equity)} b={safeNum(clientB.Equity)} format={v => fmtMoney(v)} higher="risk" />
            <CompareRow label="Profit" a={safeNum(clientA.Profit)} b={safeNum(clientB.Profit)} format={v => fmtMoney(v)} higher="risk" />
            <CompareRow label="Volume" a={safeNum(clientA.Volume)} b={safeNum(clientB.Volume)} format={v => fmtNum(v)} higher="risk" />
            <CompareRow label="Deposit" a={safeNum(clientA.Deposit)} b={safeNum(clientB.Deposit)} format={v => fmtMoney(v)} higher="neutral" />
            <CompareRow label="Profit Factor" a={safeNum(clientA.ProfitFactor)} b={safeNum(clientB.ProfitFactor)} format={v => v.toFixed(2)} higher="risk" />
            <CompareRow label="Win Rate" a={safeNum(clientA.ProfitPct)} b={safeNum(clientB.ProfitPct)} format={v => v > 0 ? v.toFixed(1) + "%" : "—"} higher="risk" />
            <CompareRow label="Long %" a={safeNum(clientA.LongPct)} b={safeNum(clientB.LongPct)} format={v => v > 0 ? v.toFixed(1) + "%" : "—"} higher="risk" />
            <CompareRow label="Avg Profit Trade" a={safeNum(clientA.AvgProfitTrade)} b={safeNum(clientB.AvgProfitTrade)} format={v => fmtMoney(v)} higher="risk" />
            <CompareRow label="Avg Loss Trade" a={safeNum(clientA.AvgLossTrade)} b={safeNum(clientB.AvgLossTrade)} format={v => fmtMoney(v)} higher="risk" />
            <CompareRow label="Max Consec. Wins" a={safeNum(clientA.MaxConsecutiveWins)} b={safeNum(clientB.MaxConsecutiveWins)} format={v => v > 0 ? v.toFixed(0) : "—"} higher="risk" />
            <CompareRow label="Best Trade" a={safeNum(clientA.BestTrade)} b={safeNum(clientB.BestTrade)} format={v => fmtMoney(v)} higher="risk" />
            <CompareRow label="Worst Trade" a={safeNum(clientA.WorstTrade)} b={safeNum(clientB.WorstTrade)} format={v => fmtMoney(v)} higher="risk" />

            <SectionHeader title="Lifetime Totals" color={T.purple} />
            <CompareRow label="T.Deposit" a={safeNum(clientA["T.Deposit"])} b={safeNum(clientB["T.Deposit"])} format={v => fmtMoney(v)} higher="neutral" />
            <CompareRow label="T.Withdraw" a={Math.abs(safeNum(clientA["T.Withdraw"]))} b={Math.abs(safeNum(clientB["T.Withdraw"]))} format={v => fmtMoney(v)} higher="neutral" />
            <CompareRow label="T.Volume" a={safeNum(clientA["T.Volume"])} b={safeNum(clientB["T.Volume"])} format={v => fmtNum(v)} higher="risk" />
            <CompareRow label="T.Profit" a={safeNum(clientA["T.Profit"])} b={safeNum(clientB["T.Profit"])} format={v => fmtMoney(v)} higher="risk" />
            <CompareRow label="Total Trades" a={safeNum(clientA.TotalTrades)} b={safeNum(clientB.TotalTrades)} format={v => fmtNum(v)} higher="risk" />

            <SectionHeader title="Trading Behavior" color={T.orange} />
            <CompareRow label="Speed <30s" a={safeNum(clientA.PctUnder30Sec)} b={safeNum(clientB.PctUnder30Sec)} format={v => v.toFixed(1) + "%"} higher="risk" />
            <CompareRow label="Speed <2min" a={safeNum(clientA.PctUnder2Min)} b={safeNum(clientB.PctUnder2Min)} format={v => v.toFixed(1) + "%"} higher="risk" />
            <CompareRow label="Speed <5min" a={safeNum(clientA.PctUnder5Min)} b={safeNum(clientB.PctUnder5Min)} format={v => v.toFixed(1) + "%"} higher="risk" />
            <CompareRow label="Hedge Ratio" a={safeNum(clientA.HedgeRatio)} b={safeNum(clientB.HedgeRatio)} format={v => v.toFixed(2)} higher="risk" />
            <CompareRow label="Hedge Volume" a={safeNum(clientA.HedgeVolumeLots)} b={safeNum(clientB.HedgeVolumeLots)} format={v => v > 0 ? v.toFixed(2) + " lots" : "—"} higher="risk" />
            <CompareRow label="Friday HB" a={safeNum(clientA.FridayHedgeBreaks)} b={safeNum(clientB.FridayHedgeBreaks)} format={v => v > 0 ? v.toFixed(0) : "—"} higher="risk" />
            <CompareRow label="Daily Volume" a={safeNum(clientA.DailyVolumeLots)} b={safeNum(clientB.DailyVolumeLots)} format={v => v.toFixed(2) + " lots"} higher="risk" />
            <CompareRow label="Trade Freq/Day" a={safeNum(clientA.TradingFreqPerDay)} b={safeNum(clientB.TradingFreqPerDay)} format={v => v > 0 ? v.toFixed(1) : "—"} higher="risk" />
            <CompareRow label="Avg Duration" a={safeNum(clientA.AvgTradeDuration)} b={safeNum(clientB.AvgTradeDuration)} format={v => v > 0 ? (v < 60 ? v.toFixed(0) + "s" : v < 3600 ? (v / 60).toFixed(1) + "m" : (v / 3600).toFixed(1) + "h") : "—"} higher="good" />
            <CompareRow label="Trade Size" a={safeNum(clientA.MostUsedTradeSize)} b={safeNum(clientB.MostUsedTradeSize)} format={v => v > 0 ? v.toFixed(2) + " lots" : "—"} higher="risk" />

            <SectionHeader title="Client Info" color={T.blue} />
            <TextRow label="Status" a={clientA.ClientStatus} b={clientB.ClientStatus} />
            <TextRow label="Account Type" a={clientA.Account_Type} b={clientB.Account_Type} />
            <TextRow label="Country" a={clientA.Country} b={clientB.Country} />
            <TextRow label="Account Manager" a={clientA["A.M"]} b={clientB["A.M"]} />
            <TextRow label="Profiling" a={clientA.ClientProfiling} b={clientB.ClientProfiling} />
            <TextRow label="Instrument" a={clientA.MostTradedInstruments} b={clientB.MostTradedInstruments} />
          </Card>

          {/* Charts Row 1: Radar + Risk Score Timeline */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Card>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Risk Breakdown</div>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke={T.border} />
                  <PolarAngleAxis dataKey="cat" tick={{ fill: T.textDim, fontSize: 11 }} />
                  <PolarRadiusAxis tick={{ fill: T.textMuted, fontSize: 9 }} />
                  <Radar name={nameA} dataKey="A" stroke={T.cyan} fill={T.cyan} fillOpacity={0.15} strokeWidth={2} />
                  <Radar name={nameB} dataKey="B" stroke={T.amber} fill={T.amber} fillOpacity={0.15} strokeWidth={2} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Risk Score Timeline</div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={timelines.risk}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="month" tick={{ fill: T.textMuted, fontSize: 9 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: T.textMuted, fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="scoreA" stroke={T.cyan} strokeWidth={2} dot={{ r: 2 }} name={nameA} connectNulls />
                  <Line type="monotone" dataKey="scoreB" stroke={T.amber} strokeWidth={2} dot={{ r: 2 }} name={nameB} connectNulls />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 4 }}>
                {[{ l: "Safe 0-15", c: "#22c55e" }, { l: "Low 16-26", c: "#06b6d4" }, { l: "Elevated 27-38", c: "#f59e0b" }, { l: "High 39-50", c: "#f97316" }, { l: "Critical 51+", c: "#ef4444" }].map(x => (
                  <span key={x.l} style={{ fontSize: 9, color: x.c, fontWeight: 600 }}>● {x.l}</span>
                ))}
              </div>
            </Card>
          </div>

          {/* Charts Row 2: Volume Timeline + Profit Timeline */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Card>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Volume Timeline</div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={timelines.volume}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="month" tick={{ fill: T.textMuted, fontSize: 9 }} />
                  <YAxis tick={{ fill: T.textMuted, fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="volA" stroke={T.cyan} strokeWidth={2} dot={{ r: 2 }} name={nameA} connectNulls />
                  <Line type="monotone" dataKey="volB" stroke={T.amber} strokeWidth={2} dot={{ r: 2 }} name={nameB} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Profit Timeline</div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={timelines.profit}>
                  <defs>
                    <linearGradient id="profGradA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset={(() => { const vals = timelines.profit.map(d => d.profA).filter(v => v != null); const mx = Math.max(...vals, 0); const mn = Math.min(...vals, 0); return mx === mn ? "50%" : ((mx / (mx - mn)) * 100).toFixed(1) + "%"; })()} stopColor="#22c55e" stopOpacity={0.25} />
                      <stop offset={(() => { const vals = timelines.profit.map(d => d.profA).filter(v => v != null); const mx = Math.max(...vals, 0); const mn = Math.min(...vals, 0); return mx === mn ? "50%" : ((mx / (mx - mn)) * 100).toFixed(1) + "%"; })()} stopColor="#ef4444" stopOpacity={0.25} />
                    </linearGradient>
                    <linearGradient id="profGradB" x1="0" y1="0" x2="0" y2="1">
                      <stop offset={(() => { const vals = timelines.profit.map(d => d.profB).filter(v => v != null); const mx = Math.max(...vals, 0); const mn = Math.min(...vals, 0); return mx === mn ? "50%" : ((mx / (mx - mn)) * 100).toFixed(1) + "%"; })()} stopColor="#22c55e" stopOpacity={0.15} />
                      <stop offset={(() => { const vals = timelines.profit.map(d => d.profB).filter(v => v != null); const mx = Math.max(...vals, 0); const mn = Math.min(...vals, 0); return mx === mn ? "50%" : ((mx / (mx - mn)) * 100).toFixed(1) + "%"; })()} stopColor="#ef4444" stopOpacity={0.15} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="month" tick={{ fill: T.textMuted, fontSize: 9 }} />
                  <YAxis tick={{ fill: T.textMuted, fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine y={0} stroke={T.textMuted} strokeDasharray="4 4" strokeWidth={1} />
                  <Area type="monotone" dataKey="profA" stroke={T.cyan} strokeWidth={2} fill="url(#profGradA)" dot={{ r: 2 }} name={nameA} connectNulls />
                  <Area type="monotone" dataKey="profB" stroke={T.amber} strokeWidth={2} fill="url(#profGradB)" dot={{ r: 2 }} name={nameB} connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Group Timeline */}
          <Card style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Group Assignment Timeline</div>
            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 0 }}>
              {[{ label: "Client A", color: T.cyan, key: "groupA" }, { label: "Client B", color: T.amber, key: "groupB" }].map(({ label, color, key }) => (
                <React.Fragment key={label}>
                  <div style={{ padding: "8px 0", fontSize: 11, color, fontWeight: 700, display: "flex", alignItems: "center" }}>{label}</div>
                  <div style={{ display: "flex", gap: 2, padding: "8px 0", overflowX: "auto" }}>
                    {timelines.group.map((g, i) => {
                      const gk = g[key];
                      const gInfo = gk ? (gcfg.groups[gk] || {}) : {};
                      return (
                        <div key={i} style={{ flex: 1, minWidth: 50, textAlign: "center" }}>
                          <div style={{ fontSize: 8, color: T.textMuted, marginBottom: 2 }}>{g.month}</div>
                          <div style={{ padding: "3px 2px", borderRadius: 4, background: gk ? (gInfo.color || T.cyan) + "25" : T.bg, fontSize: 9, fontWeight: 700, color: gInfo.color || T.textMuted, whiteSpace: "nowrap" }}>
                            {gk || "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}20` }}>
              {(gcfg.groupOrder || []).map(gk => {
                const g = gcfg.groups[gk]; if (!g) return null;
                return <span key={gk} style={{ fontSize: 10, color: g.color, fontWeight: 600 }}>● {g.name}</span>;
              })}
            </div>
          </Card>
        </div>
      ) : (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <Search size={32} color={T.textMuted} style={{ marginBottom: 12 }} />
          <div style={{ color: T.textMuted, fontSize: 14 }}>Select two clients above to compare</div>
          <div style={{ color: T.textMuted, fontSize: 11, marginTop: 4 }}>Type login number or client name in the search boxes</div>
        </Card>
      )}
    </div>
  );
}
